/**
 * PLPMobileSwipe - Mobile swipe-through-images on PLP product cards
 * Allows users to swipe left/right on product cards to browse images
 * Uses spring physics animation for natural feel
 */
window.PLPMobileSwipe = {
  initialized: new WeakSet(),
  hintPlayed: false,
  // PLPs without TagalysMediaManager (Tagalys) put each product's images here,
  // productId -> { allImages: [urls, displayed image first] } - see
  // setupMobileSwipe() in assets/tagalys-collection-filter.js.
  imageCache: new Map(),

  // Spring physics parameters
  SPRING_STIFFNESS: 253,
  SPRING_DAMPING: 32,
  SPRING_MASS: 5,
  SPRING_MAX_DURATION: 1869,

  // Touch thresholds
  SWIPE_THRESHOLD: 40,
  VELOCITY_THRESHOLD: 0.3,
  AXIS_LOCK_DISTANCE: 10,

  // Image sizing for mobile
  IMAGE_SIZE: '350x',

  init: function() {
    if (typeof isMobile === 'function' && !isMobile()) return;
    if (window.innerWidth > 767) return;
  },

  /**
   * Process product cards and attach swipe functionality
   * Called after Tagalys renders products and hover images are fetched
   */
  processCards: function(items) {
    if (typeof isMobile === 'function' && !isMobile()) return;
    if (window.innerWidth > 767) return;

    var self = this;
    items.forEach(function(card) {
      if (self.initialized.has(card)) return;

      var handle = card.getAttribute('data-product-handle');
      if (!handle) return;

      var productId = card.getAttribute('data-product-id');
      if (!productId) return;

      // Get images from TagalysMediaManager cache
      var images = [];
      if (typeof TagalysMediaManager !== 'undefined' && TagalysMediaManager.hoverImageCache) {
        var cached = TagalysMediaManager.hoverImageCache.get(String(productId));
        if (cached && cached.allImages) {
          images = cached.allImages.slice();
          // For lifestyle-first products, the 2nd image is displayed as default
          // Reorder so swipe slides match: [img2 (displayed), img1, img3, img4]
          if (cached.isLifestyleFirst && images.length >= 2) {
            var first = images[0];
            images[0] = images[1];
            images[1] = first;
          }
        }
      }

      if (!images.length && self.imageCache.has(String(productId))) {
        images = self.imageCache.get(String(productId)).allImages.slice();
      }

      // Need at least 2 images for swiping
      if (images.length < 2) return;

      // Cap at 4 images
      images = images.slice(0, 4);

      self.buildSwipeDOM(card, images);
      self.attachTouchHandlers(card);
      self.initialized.add(card);
    });

    // Play swipe hint on the first visible card (once per page load)
    if (!this.hintPlayed) {
      this.hintPlayed = true;
      var self = this;
      setTimeout(function() {
        // Query fresh DOM elements (Tagalys may have re-rendered since processCards)
        var freshCards = Array.prototype.slice.call(document.querySelectorAll('[data-product-id]'));
        self.playSwipeHint(freshCards);
        self.setupPreloader(freshCards);
      }, 1500);
    }
  },

  /**
   * Build swipe DOM structure inside the product card image wrapper
   */
  buildSwipeDOM: function(card, imageUrls) {
    var wrapper = card.querySelector('.wb-prdct20-fashion__image-wrapper');
    if (!wrapper) return;

    // Mark as swipe-active
    var imageContainer = card.querySelector('.wb-prdct20-fashion__image-container');
    if (imageContainer) {
      imageContainer.classList.add('plp-swipe-active');
    }

    // Capture slide width now (wrapper is laid out at this point).
    // Using pixels makes track/slide sizing independent of flex-shrink behaviour.
    var slideWidth = wrapper.offsetWidth;

    // Get existing picture element (first image)
    var existingPicture = wrapper.querySelector('picture');

    // Create swipe track
    var track = document.createElement('div');
    track.className = 'plp-swipe-track';
    track.style.width = (imageUrls.length * slideWidth) + 'px';

    // First slide: wrap existing picture
    var firstSlide = document.createElement('div');
    firstSlide.className = 'plp-swipe-slide';
    firstSlide.style.width = slideWidth + 'px';
    if (existingPicture) {
      firstSlide.appendChild(existingPicture);
    }
    track.appendChild(firstSlide);

    // Additional slides: simple img tags with data-src for lazy loading
    for (var i = 1; i < imageUrls.length; i++) {
      var slide = document.createElement('div');
      slide.className = 'plp-swipe-slide';
      slide.style.width = slideWidth + 'px';
      var img = document.createElement('img');
      img.className = 'plp-swipe-img';
      img.setAttribute('data-src', this.getSizedUrl(imageUrls[i], this.IMAGE_SIZE));
      img.alt = '';
      slide.appendChild(img);
      track.appendChild(slide);
    }

    // Create pagination dots (always visible)
    var dots = document.createElement('div');
    dots.className = 'plp-swipe-dots plp-swipe-dots--visible';
    for (var j = 0; j < imageUrls.length; j++) {
      var dot = document.createElement('span');
      dot.className = 'plp-swipe-dot' + (j === 0 ? ' plp-swipe-dot--active' : '');
      dots.appendChild(dot);
    }

    // Insert into wrapper
    wrapper.appendChild(track);
    wrapper.appendChild(dots);

    // Store state on the card
    card._swipe = {
      track: track,
      dots: dots,
      currentIndex: 0,
      totalSlides: imageUrls.length,
      animating: false,
      slideWidth: slideWidth
    };
  },

  /**
   * Attach touch event handlers to a product card
   */
  attachTouchHandlers: function(card) {
    var self = this;
    var wrapper = card.querySelector('.wb-prdct20-fashion__image-wrapper');
    if (!wrapper) return;

    var startX = 0;
    var startY = 0;
    var startTime = 0;
    var currentX = 0;
    var isHorizontal = null;
    var didSwipe = false;
    var containerWidth = 0;

    wrapper.addEventListener('touchstart', function(e) {
      if (card._swipe.animating) return;

      var touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      currentX = 0;
      isHorizontal = null;
      didSwipe = false;
      containerWidth = wrapper.offsetWidth || card._swipe.slideWidth;
    }, { passive: true });

    wrapper.addEventListener('touchmove', function(e) {
      if (card._swipe.animating) return;
      if (isHorizontal === false) return;

      var touch = e.touches[0];
      var deltaX = touch.clientX - startX;
      var deltaY = touch.clientY - startY;

      // Determine axis lock
      if (isHorizontal === null) {
        var totalMove = Math.abs(deltaX) + Math.abs(deltaY);
        if (totalMove > self.AXIS_LOCK_DISTANCE) {
          isHorizontal = Math.abs(deltaX) > Math.abs(deltaY);
          if (!isHorizontal) return;
        } else {
          return;
        }
      }

      e.preventDefault();
      didSwipe = true;
      currentX = deltaX;

      // Clamp at boundaries (no rubber-band)
      var swipe = card._swipe;
      var baseOffset = -swipe.currentIndex * containerWidth;
      var targetOffset = baseOffset + deltaX;
      var maxOffset = 0;
      var minOffset = -(swipe.totalSlides - 1) * containerWidth;

      targetOffset = Math.max(minOffset, Math.min(maxOffset, targetOffset));

      swipe.track.style.transform = 'translateX(' + targetOffset + 'px)';
    }, { passive: false });

    wrapper.addEventListener('touchend', function() {
      if (isHorizontal !== true) return;
      if (card._swipe.animating) return;

      var swipe = card._swipe;
      var elapsed = Date.now() - startTime;
      var velocity = Math.abs(currentX) / elapsed;
      var newIndex = swipe.currentIndex;

      if (Math.abs(currentX) > self.SWIPE_THRESHOLD || velocity > self.VELOCITY_THRESHOLD) {
        if (currentX < 0 && swipe.currentIndex < swipe.totalSlides - 1) {
          newIndex = swipe.currentIndex + 1;
        } else if (currentX > 0 && swipe.currentIndex > 0) {
          newIndex = swipe.currentIndex - 1;
        }
      }

      // Lazy load the target slide image
      self.lazyLoadSlide(swipe.track, newIndex);

      var toX = -newIndex * containerWidth;

      swipe.currentIndex = newIndex;
      self.updateDots(card);
      self.animateSlide(swipe, toX);
    }, { passive: true });

    // Prevent link navigation when swiping
    wrapper.addEventListener('click', function(e) {
      if (didSwipe) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  },

  /**
   * Animate slide transition using CSS transition
   */
  animateSlide: function(swipe, toX) {
    var track = swipe.track;
    swipe.animating = true;

    track.style.transition = 'transform 250ms ease-out';
    track.style.transform = 'translateX(' + toX + 'px)';

    function onEnd() {
      track.removeEventListener('transitionend', onEnd);
      track.style.transition = '';
      swipe.animating = false;
    }

    track.addEventListener('transitionend', onEnd);

    // Fallback in case transitionend doesn't fire
    setTimeout(function() {
      track.style.transition = '';
      swipe.animating = false;
    }, 300);
  },

  /**
   * Update pagination dots to reflect current slide
   */
  updateDots: function(card) {
    var swipe = card._swipe;
    if (!swipe || !swipe.dots) return;

    var dots = swipe.dots.children;
    for (var i = 0; i < dots.length; i++) {
      if (i === swipe.currentIndex) {
        dots[i].classList.add('plp-swipe-dot--active');
      } else {
        dots[i].classList.remove('plp-swipe-dot--active');
      }
    }
  },

  /**
   * Lazy load an image in a specific slide
   */
  lazyLoadSlide: function(track, index) {
    // Load target slide + preload next slide
    for (var i = index; i <= index + 1; i++) {
      var slide = track.children[i];
      if (!slide) continue;

      var img = slide.querySelector('img[data-src]');
      if (img && !img.src) {
        img.src = img.getAttribute('data-src');
        img.removeAttribute('data-src');
        img.onload = function() {
          this.classList.add('plp-swipe-img--loaded');
        };
      }
    }
  },

  /**
   * Play a subtle swipe hint on the first visible product card
   */
  playSwipeHint: function(items) {
    // Find the first card that is in the viewport
    var card = null;
    for (var i = 0; i < items.length; i++) {
      if (!items[i]._swipe) continue;
      var rect = items[i].getBoundingClientRect();
      if (rect.top >= 0 && rect.top < window.innerHeight) {
        card = items[i];
        break;
      }
    }
    if (!card) return;

    var swipe = card._swipe;
    var track = swipe.track;
    var wrapper = card.querySelector('.wb-prdct20-fashion__image-wrapper');
    // Same measurement the working touch handlers use
    var slideWidth = wrapper ? wrapper.offsetWidth : 0;
    if (!slideWidth) return;
    var dragPx = Math.round(slideWidth * 0.22);

    function startHintAnimation() {
      // Short drag left
      track.style.transition = 'transform 350ms cubic-bezier(0.4, 0, 0.2, 1)';
      track.style.transform = 'translateX(-' + dragPx + 'px)';

      setTimeout(function() {
        // Spring back with slight overshoot
        track.style.transition = 'transform 400ms cubic-bezier(0.34, 1.3, 0.64, 1)';
        track.style.transform = 'translateX(0px)';

        setTimeout(function() {
          track.style.transition = '';
          track.style.transform = '';
        }, 400);
      }, 550);
    }

    // Preload slide 2 at full resolution, then start animation
    var slide2 = track.children[1];
    var img = slide2 ? slide2.querySelector('img[data-src]') : null;
    if (img) {
      img.src = img.getAttribute('data-src');
      img.removeAttribute('data-src');
      img.onload = function() {
        img.classList.add('plp-swipe-img--loaded');
        startHintAnimation();
      };
      // Skip hint if image takes too long
      setTimeout(function() {
        if (!img.complete) {
          img.onload = null;
        }
      }, 5000);
    } else {
      startHintAnimation();
    }
  },

  /**
   * Preload slide 2 image for a single card
   */
  preloadSlide2: function(card) {
    if (!card._swipe) return;
    var slide2 = card._swipe.track.children[1];
    if (!slide2) return;
    var img = slide2.querySelector('img[data-src]');
    if (!img) return;
    img.src = img.getAttribute('data-src');
    img.removeAttribute('data-src');
    img.onload = function() {
      img.classList.add('plp-swipe-img--loaded');
    };
  },

  /**
   * Set up IntersectionObserver to proactively preload slide 2 as cards enter viewport
   */
  setupPreloader: function(items) {
    var self = this;
    setTimeout(function() {
      if (!('IntersectionObserver' in window)) {
        // Fallback for old browsers: preload first 10 only
        items.slice(0, 10).forEach(function(card) {
          self.preloadSlide2(card);
        });
        return;
      }
      // Preload in idle time: on phones every first-row card otherwise fetched
      // its second photo while the LCP image was still downloading.
      var scheduleIdle = typeof window.requestIdleCallback === 'function'
        ? function (fn) { window.requestIdleCallback(fn, { timeout: 2000 }); }
        : function (fn) { setTimeout(fn, 300); };
      var observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            var card = entry.target;
            scheduleIdle(function () { self.preloadSlide2(card); });
            observer.unobserve(card);
          }
        });
      }, { rootMargin: '200px' });

      items.forEach(function(card) {
        if (card._swipe) observer.observe(card);
      });
    }, 500); // setupPreloader is already called inside a 1.5s timeout, 500ms extra = 2s total
  },

  /**
   * Add size suffix to Shopify CDN image URL
   */
  getSizedUrl: function(url, size) {
    if (!url || !size) return url;

    var extensions = ['.png?', '.jpg?', '.gif?'];
    for (var i = 0; i < extensions.length; i++) {
      if (url.indexOf(extensions[i]) !== -1) {
        return url.replace(extensions[i], '_' + size + extensions[i]);
      }
    }
    return url;
  },

  /**
   * Manually trigger swipe hint from console: PLPMobileSwipe.debug()
   */
  debug: function() {
    this.hintPlayed = false;
    var cards = document.querySelectorAll('[data-product-id]');
    this.playSwipeHint(Array.prototype.slice.call(cards));
  }
};
